<template>
  <modal :show.sync="visible" @update:show="handleModalClose" class="dark-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ $t('users.filter.title') }}</h2>
      </div>

      <div class="source-type-filter-content">
        <!-- Format Type Section -->
        <div class="field source-type-filter-section">
          <label class="label source-type-filter-section-label">{{ $t('sourceTypes.filter.title') }}</label>
          <div class="field is-grouped is-grouped-multiline source-type-filter-options">
            <div class="control" v-for="format in availableFormats" :key="format.value">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="format.value"
                  v-model="localFilters.types"
                  @change="updateResultCount"
                >
                {{ format.label }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="button cancel-button"
          @click="clearAllFilters"
        >
          {{ $t('users.filter.clearAll') }}
        </button>
        <button
          type="button"
          class="button is-primary"
          @click="confirmFilters"
          :disabled="isLoading"
          :class="{ 'is-loading': isLoading }"
        >
          {{ $t('users.filter.confirm') }}
          <span v-if="resultCount !== null" class="tag is-light ml-2">
            {{ resultCount }}
          </span>
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Modal } from '@cyradar/ui'
import { debounce } from '@/utils'

export default {
  name: 'SourceTypeFilter',
  components: {
    Modal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({
        types: []
      })
    }
  },
  data() {
    return {
      visible: false,
      localFilters: {
        types: []
      },
      resultCount: null,
      isLoading: false
    }
  },
  computed: {
    /**
     * Available format type options for filtering
     */
    availableFormats() {
      return [
        { value: 'json', label: 'JSON' },
        { value: 'csv', label: 'CSV' },
        { value: 'xml', label: 'XML' },
        { value: 'syslog', label: 'SYSLOG' },
        { value: 'cef', label: 'CEF' },
        { value: 'custom', label: this.$t('sourceTypes.filter.custom') }
      ]
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        this.visible = newVal
        if (newVal) {
          this.initializeFilters()
        }
      }
    },
    filters: {
      deep: true,
      immediate: true,
      handler(newFilters) {
        if (newFilters) {
          this.localFilters = {
            types: [...(newFilters.types || [])]
          }
        }
      }
    }
  },
  created() {
    // Debounced function for updating result count
    this.debouncedUpdateResultCount = debounce(this.fetchResultCount, 500)
  },
  methods: {
    /**
     * Initialize filters when modal opens
     */
    initializeFilters() {
      this.localFilters = {
        types: [...(this.filters.types || [])]
      }
      this.resultCount = null
    },
    /**
     * Handle modal close event
     */
    handleModalClose(show) {
      this.visible = show
      this.$emit('update:show', show)
      if (!show) {
        this.resultCount = null
      }
    },
    /**
     * Update result count when filters change
     */
    updateResultCount() {
      if (this.hasActiveFilters()) {
        this.debouncedUpdateResultCount()
      } else {
        this.resultCount = null
      }
    },
    /**
     * Fetch result count based on current filters
     */
    async fetchResultCount() {
      if (!this.hasActiveFilters()) {
        this.resultCount = null
        return
      }

      this.isLoading = true
      try {
        const params = this.buildFilterParams()
        params.limit = 1 // We only need the count, not the actual data

        const response = await this.$http.get('/api/v1/source-types', { params })
        if (response && response.data && response.data.data) {
          this.resultCount = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching result count:', error)
        this.resultCount = null
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Build filter parameters for API request
     */
    buildFilterParams() {
      const params = {}

      // Convert types filter to API format - send as comma-separated string
      if (this.localFilters.types.length > 0) {
        params.types = this.localFilters.types.join(',')
      }
      // If empty array, don't add types parameter (show all)

      return params
    },
    /**
     * Check if any filters are currently active
     */
    hasActiveFilters() {
      return this.localFilters.types.length > 0
    },
    /**
     * Confirm and apply filters
     */
    confirmFilters() {
      this.$emit('apply-filters', {
        types: [...this.localFilters.types],
        resultCount: this.resultCount // Pass the result count to parent
      })
      // Close modal after a slight delay to ensure event processing
      this.$nextTick(() => {
        this.handleModalClose(false)
      })
    },
    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.localFilters = {
        types: []
      }
      this.resultCount = null
    },
    /**
     * Cancel filter changes
     */
    cancelFilters() {
      this.handleModalClose(false)
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

.source-type-filter-content {
  padding: 1rem 0;
}

.source-type-filter-section {
  margin-bottom: 1.5rem;
}

.source-type-filter-section-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: $modal-text;
}

.source-type-filter-options {
  margin-top: 0.5rem;
}

.source-type-filter-options .control {
  margin-right: 1rem;
  margin-bottom: 0.5rem;
}

.source-type-filter-options .checkbox {
  font-weight: normal;
  color: $modal-text;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style>
